import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ChangePasswordOverlay from "../components/auth/ChangePasswordOverlay";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { fetchCurrentUser, updateBasketComparisonUsage, updateProfile } from "../store/auth/authSlice";
import { fetchPlans } from "../store/plans/plansSlice";
import { fetchCurrentSubscription } from "../store/billing/billingSlice";
import {
  createGroceryList,
  fetchGroceryLists,
  clearCreateGroceryListState,
  fetchGroceryListDetail,
  updateGroceryList,
  deleteGroceryList,
  clearUpdateGroceryListState,
  clearDeleteGroceryListState,
  addGroceryProduct,
  updateGroceryProduct,
  deleteGroceryProduct,
  clearAddGroceryProductState,
  clearUpdateGroceryProductState,
  clearDeleteGroceryProductState,
} from "../store/groceryLists/groceryListsSlice";
import { API_BASE_URL, apiRequest, getAuthHeader } from "../api/client";
import { useToast } from "../components/ToastProvider";
import {
  getGroceryProductNavigation,
  normalizeGroceryProductPlatform,
  rememberGroceryProductView,
} from "../utils/groceryProductView";
import { fetchGroceryBasketSummary, savePasteListFlow } from "../utils/pasteListFlow";

const tabs = [
  { id: "public", label: "Public Profile" },
  { id: "billing", label: "Billings & Plans" },
  { id: "grocery", label: "My Grocery Lists" },
];

function groceryListIdFromSearch(search) {
  const params = new URLSearchParams(search || "");
  return params.get("grocery_list_id") || params.get("groceryListId") || params.get("id");
}

function sortGroceryProductsByCreatedAt(items) {
  return [...(Array.isArray(items) ? items : [])].sort((a, b) => {
    const aTime = a?.created_at ? new Date(a.created_at).getTime() : Number(a?.id) || 0;
    const bTime = b?.created_at ? new Date(b.created_at).getTime() : Number(b?.id) || 0;
    return aTime - bTime;
  });
}

function grocerySettingsPath({ view = "lists", groceryListId = null } = {}) {
  const params = new URLSearchParams({ tab: "grocery" });
  if (view === "create") {
    params.set("view", "create");
  } else if (view === "detail" && groceryListId) {
    params.set("view", "detail");
    params.set("grocery_list_id", String(groceryListId));
  } else {
    params.set("view", "lists");
  }
  return `/settings?${params.toString()}`;
}

function SettingsTabsNav({ activeTab, onTabClick }) {
  return (
    <nav className="space-y-2 text-[14px]">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => onTabClick(tab.id)}
          className={`flex w-full items-center rounded-full px-4 py-2 text-left text-[16px] font-bold transition ${activeTab === tab.id
            ? "bg-[#7352C7] text-white"
            : "bg-transparent text-[#6E7B86] hover:bg-femty-bg"
            }`}
        >
          <span>{tab.label}</span>
        </button>
      ))}
    </nav>
  );
}

function SettingsPageStyles() {
  return (
    <style>{`
      /* Settings page UI alignment + colors */
      .settings-page-root {
        display: flex !important;
        flex-direction: column !important;
      }

      .settings-page-root > main {
        max-width: 80rem !important; /* ~max-w-7xl */
        flex: 1 1 auto !important;
        min-height: unset !important;
        width: 100% !important;
      }

      @media (min-width: 768px) {
        .settings-page-root > main {
          padding-left: 2.5rem !important; /* md:px-10 */
          padding-right: 2.5rem !important;
        }
      }
    `}</style>
  );
}

function Settings({ onOpenAuth, onOpenMenu }) {
  const [activeTab, setActiveTab] = useState("public");
  const [groceryView, setGroceryView] = useState("lists"); // lists | create | detail
  const [groceryPrevView, setGroceryPrevView] = useState("lists");
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [selectedGroceryListId, setSelectedGroceryListId] = useState(null);
  const [mobileSettingsNavOpen, setMobileSettingsNavOpen] = useState(false);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const token = useAppSelector((s) => s.auth.token);
  const user = useAppSelector((s) => s.auth.user);
  const isLoggedIn = Boolean(token);
  const [heldGroceryDetailId, setHeldGroceryDetailId] = useState(null);
  const seededGroceryBackRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    const view = params.get("view");
    const groceryListId = groceryListIdFromSearch(location.search);

    if (tab === "billing" || tab === "grocery") {
      setActiveTab(tab);
    } else if (!tab) {
      setActiveTab("public");
    }

    if (tab === "grocery") {
      if (groceryListId) {
        setSelectedGroceryListId(groceryListId);
        setGroceryView("detail");
      } else if (view === "create") {
        setSelectedGroceryListId(null);
        setGroceryView("create");
      } else {
        setSelectedGroceryListId(null);
        setGroceryView("lists");
      }
    }
  }, [location.search]);

  // Deep links to grocery detail (e.g. from Home) have no lists entry in
  // history. Insert one so browser Back returns to the list page.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const groceryListId = groceryListIdFromSearch(location.search);
    const isDetail = params.get("tab") === "grocery" && Boolean(groceryListId);

    if (!isDetail) {
      seededGroceryBackRef.current = false;
      return;
    }
    if (location.state?.fromGroceryLists || location.state?.pendingGroceryDetailId) {
      return;
    }
    if (seededGroceryBackRef.current) return;
    seededGroceryBackRef.current = true;
    setHeldGroceryDetailId(groceryListId);
    navigate(grocerySettingsPath({ view: "lists" }), {
      replace: true,
      state: { pendingGroceryDetailId: groceryListId },
    });
  }, [location.search, location.state, navigate]);

  useEffect(() => {
    const pendingId = location.state?.pendingGroceryDetailId;
    if (!pendingId) return;
    navigate(grocerySettingsPath({ view: "detail", groceryListId: pendingId }), {
      state: { fromGroceryLists: true },
    });
  }, [location.state, navigate]);

  useEffect(() => {
    if (!heldGroceryDetailId) return;
    if (groceryListIdFromSearch(location.search)) {
      setHeldGroceryDetailId(null);
    }
  }, [heldGroceryDetailId, location.search]);

  useEffect(() => {
    if (activeTab === "public" && isLoggedIn && !user) {
      dispatch(fetchCurrentUser());
    }
  }, [activeTab, isLoggedIn, user, dispatch]);

  useEffect(() => {
    if (!isLoggedIn) return;
    if (activeTab !== "grocery") return;
    dispatch(fetchGroceryLists({ force: true }));
  }, [activeTab, isLoggedIn, dispatch]);

  useEffect(() => {
    if (!mobileSettingsNavOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function onKeyDown(e) {
      if (e.key === "Escape") setMobileSettingsNavOpen(false);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [mobileSettingsNavOpen]);

  function goToGroceryView(nextView, { listId = null, replace = false } = {}) {
    setGroceryPrevView(groceryView);
    if (nextView === "detail") {
      const id = listId ?? selectedGroceryListId;
      setSelectedGroceryListId(id);
      navigate(grocerySettingsPath({ view: "detail", groceryListId: id }), {
        replace,
        state: { fromGroceryLists: true },
      });
      return;
    }
    if (nextView !== "create") {
      setSelectedGroceryListId(null);
    }
    navigate(grocerySettingsPath({ view: nextView || "lists" }), { replace });
  }

  function handleSettingsTabClick(tabId) {
    if (tabId === "grocery") {
      navigate(grocerySettingsPath({ view: "lists" }));
    } else if (tabId === "public") {
      navigate("/settings");
    } else {
      navigate(`/settings?tab=${tabId}`);
    }
    setMobileSettingsNavOpen(false);
  }

  if (!isLoggedIn) {
    return (
      <div className="settings-page-root min-h-screen flex flex-col bg-femty-bg text-femty-text">
        <SettingsPageStyles />
        <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />
        <main className="mx-auto w-full max-w-7xl flex-1 items-center justify-center px-4 py-40 md:px-10 min-h-[calc(100vh-120px)]">
          <div className="w-full max-w-md rounded-2xl border border-[#E6EAF0] bg-white px-8 py-10 text-center shadow-sm justify-self-center">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-femty-bg">
              <svg
                viewBox="0 0 24 24"
                className="h-8 w-8 text-femty-muted"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0z" />
                <path d="M12 14a5 5 0 0 0-5 5v2a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-2a5 5 0 0 0-5-5h-2z" />
              </svg>
            </div>
            <h2 className="text-[20px] font-semibold text-femty-text">
              Sign in to access your profile
            </h2>
            <p className="mt-2 text-[16px] text-femty-muted">
              Manage your account, address, and preferences after signing in.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button
                type="button"
                onClick={() => onOpenAuth("login")}
                className="w-full rounded-full border border-[#E6EAF0] bg-white px-6 py-1.5 text-[16px] font-semibold text-femty-text"
              >
                Sign In
              </button>
              <button
                type="button"
                onClick={() => onOpenAuth("signup")}
                className="w-full rounded-full bg-[#7352C7] px-6 py-1.5 text-[16px] font-semibold text-white"
              >
                Sign Up
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="settings-page-root min-h-screen flex flex-col bg-femty-bg text-femty-text">
      <SettingsPageStyles />
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />
      <div className="flex min-h-[calc(100vh-120px)] flex-1 flex-col gap-4 px-3 py-6 sm:px-4 md:flex-row md:gap-10 md:px-10 md:py-10">
        <div className="flex shrink-0 items-center justify-between md:hidden">
          <h2 className="text-[20px] font-semibold text-[#24303A]">Settings</h2>
          <button
            type="button"
            className="rounded-full border border-[#E6EAF0] bg-white p-2 text-[#24303A] shadow-sm hover:bg-femty-bg"
            aria-expanded={mobileSettingsNavOpen}
            aria-haspopup="dialog"
            aria-label="Open settings sections"
            onClick={() => setMobileSettingsNavOpen(true)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              className="h-6 w-6"
              fill="currentColor"
              aria-hidden
            >
              <circle cx="12" cy="6" r="1.75" />
              <circle cx="12" cy="12" r="1.75" />
              <circle cx="12" cy="18" r="1.75" />
            </svg>
          </button>
        </div>

        <aside className="hidden w-56 shrink-0 rounded-2xl border border-[#E6EAF0] bg-white px-5 py-6 text-[16px] md:block">
          <h2 className="mb-6 text-[20px] font-semibold text-[#24303A]">
            Settings
          </h2>
          <SettingsTabsNav activeTab={activeTab} onTabClick={handleSettingsTabClick} />
        </aside>

        <section className="min-w-0 flex-1 space-y-4">
          {activeTab === "public" && (
            <PublicProfilePanel onOpenChangePassword={() => setChangePasswordOpen(true)} />
          )}
          {activeTab === "billing" && <BillingPlansPanel />}
          {activeTab === "grocery" && (
            <GrocerySettingsPanel
              view={heldGroceryDetailId ? "detail" : groceryView}
              previousView={groceryPrevView}
              onChangeView={(nextView, listId) => {
                const replace = nextView === "lists" && groceryView !== "lists";
                goToGroceryView(nextView, { listId, replace });
              }}
              selectedId={heldGroceryDetailId || selectedGroceryListId}
              onSelectId={setSelectedGroceryListId}
              onBackDetail={() => {
                goToGroceryView("lists", { replace: true });
              }}
            />
          )}
        </section>
      </div>

      <Footer />

      {mobileSettingsNavOpen ? (
        <div
          className="fixed inset-0 z-[60] md:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Settings sections"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close settings menu"
            onClick={() => setMobileSettingsNavOpen(false)}
          />
          <div className="absolute left-0 top-0 flex h-full w-[min(17.5rem,88vw)] flex-col border-r border-[#E6EAF0] bg-white px-5 py-6 shadow-xl">
            <div className="mb-6 flex items-center justify-between gap-3">
              <h2 className="text-[20px] font-semibold text-[#24303A]">Settings</h2>
              <button
                type="button"
                className="shrink-0 rounded-full p-2 text-[#6E7B86] hover:bg-femty-bg"
                aria-label="Close menu"
                onClick={() => setMobileSettingsNavOpen(false)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <SettingsTabsNav activeTab={activeTab} onTabClick={handleSettingsTabClick} />
          </div>
        </div>
      ) : null}

      <ChangePasswordOverlay
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </div>
  );
}

function PanelShell({ title, desc, action, children, className = "" }) {
  return (
    <div
      className={`h-full rounded-2xl border border-[#E6EAF0] bg-white px-4 py-5 sm:px-6 md:px-7 md:py-6 ${className}`.trim()}
    >
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between md:items-center">
        <h1 className="shrink-0 text-[20px] font-semibold text-[#24303A]">{title}</h1>
        {action ? <div className="w-full min-w-0 max-w-full sm:w-auto">{action}</div> : null}
      </div>
      {desc ? <p className="mb-3 text-[16px] font-normal text-[#6E7B86]">{desc}</p> : null}
      {children}
    </div>
  );
}

function formatGbp(value) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return "£0.00";
  return `£${n.toFixed(2)}`;
}

function toCount(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function getBasketComparisonUsage(profile) {
  if (!profile || typeof profile !== "object") return null;

  const source =
    profile.basket_comparison ||
    profile.basket_comparisons ||
    profile.comparison_usage ||
    profile;

  const unlimited = source.unlimited === true;
  let used = toCount(source.used);
  let remaining = toCount(source.remaining);
  let monthlyLimit = toCount(source.monthly_limit ?? source.monthlyLimit);

  if (remaining == null && monthlyLimit != null && used != null) {
    remaining = Math.max(0, monthlyLimit - used);
  }
  if (monthlyLimit == null && used != null && remaining != null) {
    monthlyLimit = used + remaining;
  }

  return { unlimited, used, remaining, monthlyLimit };
}

function formatComparisonCount(value) {
  return value == null ? "0" : String(value);
}

/** Parse budget input (strips £ / commas). Returns null if invalid. */
function parseBudgetAmount(value) {
  if (value === "" || value == null) return "";
  const cleaned = String(value).replace(/£/g, "").replace(/,/g, "").trim();
  if (cleaned === "") return "";
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/** Format a budget for input display as 2 d.p. (e.g. "20.00"). Empty stays empty. */
function formatBudgetAmountForInput(value) {
  const parsed = parseBudgetAmount(value);
  if (parsed === "" || parsed == null) return "";
  return parsed.toFixed(2);
}

function groceryListFormValues(detail) {
  const source =
    detail?.data && typeof detail.data === "object" && !Array.isArray(detail.data)
      ? { ...detail, ...detail.data }
      : detail || {};
  const rawTitle = source.title ?? source.name;
  const title =
    rawTitle == null || String(rawTitle).trim() === "" || String(rawTitle).toLowerCase() === "null"
      ? ""
      : String(rawTitle);
  const rawBudget = source.budget ?? source.amount;
  const budget =
    rawBudget == null || String(rawBudget).trim() === "" || String(rawBudget).toLowerCase() === "null"
      ? ""
      : formatBudgetAmountForInput(rawBudget);
  return { title, budget };
}

function sameGroceryListId(a, b) {
  if (a == null || b == null || a === "" || b === "") return false;
  return String(a) === String(b);
}

function GroceryListStructureIcon({ className = "h-10 w-10" }) {
  return (
    <svg
      viewBox="0 0 48 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M10 4H38C40.2 4 42 5.8 42 8V56C42 58.2 40.2 60 38 60H10C7.8 60 6 58.2 6 56V8C6 5.8 7.8 4 10 4Z"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinejoin="round"
      />
      <path
        d="M6 14H4M6 18H5"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
      />
      {[12, 22, 32, 42, 52].map((y) => (
        <g key={y}>
          <rect
            x="11"
            y={y - 4.5}
            width="7.5"
            height="9"
            rx="1.75"
            stroke="currentColor"
            strokeWidth="1.85"
          />
          <line
            x1="22.5"
            y1={y - 1.5}
            x2="37"
            y2={y - 1.5}
            stroke="currentColor"
            strokeWidth="1.85"
            strokeLinecap="round"
          />
          <line
            x1="22.5"
            y1={y + 2.5}
            x2="31"
            y2={y + 2.5}
            stroke="currentColor"
            strokeWidth="1.85"
            strokeLinecap="round"
          />
        </g>
      ))}
    </svg>
  );
}

function PublicProfilePanel({ onOpenChangePassword }) {
  const user = useAppSelector((s) => s.auth.user);
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [imageError, setImageError] = useState("");
  const [imageLoading, setImageLoading] = useState(false);
  const [showDeleteImageConfirm, setShowDeleteImageConfirm] = useState(false);
  const [imageCacheBust, setImageCacheBust] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user) {
      setFullName(user.full_name || "");
      setEmail(user.email || "");
      setAddressLine1(user.address_line1 || "");

    }
  }, [user]);

  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState("");

  async function handleSave() {
    setSaveError("");
    setSaveLoading(true);
    try {
      await dispatch(
        updateProfile({
          full_name: fullName.trim(),
          address_line1: addressLine1.trim() || null,

        })
      ).unwrap();
      showToast({
        type: "success",
        title: "Profile Updated",
        message: "Your profile details were saved successfully.",
      });
    } catch (err) {
      const message = typeof err === "string" ? err : "Failed to update profile.";
      setSaveError(message);
      showToast({ type: "error", title: "Update Failed", message });
    } finally {
      setSaveLoading(false);
    }
  }

  async function handleImageChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageError("");
    setImageLoading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);

      const res = await fetch(`${API_BASE_URL}api/profile/image`, {
        method: "POST",
        headers: {
          ...getAuthHeader(),
        },
        body: formData,
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg = Array.isArray(data?.errors)
          ? data.errors.join("\n")
          : data?.message || "Failed to update profile image.";
        setImageError(msg);
        showToast({ type: "error", title: "Image Upload Failed", message: msg });
      } else {
        setImageCacheBust(Date.now());
        dispatch(fetchCurrentUser({ force: true }));
        showToast({
          type: "success",
          title: "Picture Updated",
          message: "Profile picture updated successfully.",
        });
      }
    } catch (err) {
      setImageError("Network error. Please try again.");
      showToast({
        type: "error",
        title: "Network Error",
        message: "Could not upload image. Please try again.",
      });
    } finally {
      setImageLoading(false);
      if (e.target) e.target.value = "";
    }
  }

  async function handleDeleteImage() {
    setImageError("");
    setImageLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/profile/image`, {
        method: "DELETE",
        headers: {
          ...getAuthHeader(),
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg = Array.isArray(data?.errors)
          ? data.errors.join("\n")
          : data?.message || "Failed to delete profile image.";
        setImageError(msg);
        showToast({ type: "error", title: "Delete Failed", message: msg });
      } else {
        setImageCacheBust(Date.now());
        dispatch(fetchCurrentUser({ force: true }));
        showToast({
          type: "success",
          title: "Picture Deleted",
          message: "Profile picture deleted successfully.",
        });
      }
    } catch (err) {
      setImageError("Network error. Please try again.");
      showToast({
        type: "error",
        title: "Network Error",
        message: "Could not delete image. Please try again.",
      });
    } finally {
      setImageLoading(false);
    }
  }

  return (
    <PanelShell
      title="Public Profile"
      action={
        <button
          type="button"
          onClick={onOpenChangePassword}
          className="hidden self-start items-center gap-1.5 rounded-full bg-[#7352C7] px-5 py-2 text-[16px] font-semibold text-white sm:flex"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          >
            <rect
              x="5"
              y="11"
              width="14"
              height="10"
              rx="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M8 11V8a4 4 0 118 0v3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          Change Password
        </button>
      }
    >
      <div className="flex flex-col gap-8">
        <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
          <div className="order-2 flex h-24 w-24 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-[16px] font-semibold text-slate-500 sm:order-1">
            {user?.image_url ? (
              <img
                src={
                  (() => {
                    const raw = user.image_url.startsWith("http")
                      ? user.image_url
                      : `${API_BASE_URL.replace(/\/$/, "")}${user.image_url}`;
                    const hasQuery = raw.includes("?");
                    return `${raw}${hasQuery ? "&" : "?"}v=${imageCacheBust || 0}`;
                  })()
                }
                alt={user.full_name || "Profile"}
                className="h-full w-full object-cover"
              />
            ) : (
              <span>IMG</span>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageChange}
          />
          <div className="order-1 space-y-2 text-[16px] sm:order-2">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={onOpenChangePassword}
                className="flex items-center gap-1.5 rounded-full bg-[#7352C7] px-4 py-1.5 text-[14px] font-semibold text-white sm:hidden"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                >
                  <rect
                    x="5"
                    y="11"
                    width="14"
                    height="10"
                    rx="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8 11V8a4 4 0 118 0v3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Change Password
              </button>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={imageLoading}
                className="flex items-center gap-1.5 rounded-full bg-[#7352C7] px-4 py-1.5 text-[14px] font-semibold text-white hover:bg-[#7352C7]-hover disabled:cursor-not-allowed disabled:opacity-60 sm:px-5 sm:text-[16px]"
              >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 12V4M8 8l4-4 4 4"
                />
              </svg>
              {imageLoading ? "Uploading..." : "Change picture"}
            </button>
            </div>
            <button
              type="button"
              onClick={() => setShowDeleteImageConfirm(true)}
              disabled={imageLoading}
              className="flex items-center gap-1.5 rounded-full border border-red-500 px-4 py-1.5 text-[14px] font-semibold text-[#FF3C3F] disabled:cursor-not-allowed disabled:opacity-60 sm:px-5 sm:text-[16px]"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 text-red-500"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 7h12M9 7V5h6v2M10 11v6M14 11v6M5 7l1 14h12l1-14"
                />
              </svg>

              Delete picture
            </button>
            {imageError && (
              <p className="mt-1 max-w-xs text-[11px] text-red-600">
                {imageError}
              </p>
            )}
          </div>
        </div>

        <div className="flex-1 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1 text-[16px]">
              <label className="block text-[16px] font-medium text-[#24303A]">
                Full Name
              </label>
              <input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full rounded-full border border-slate-300 bg-white px-3 py-2 text-[16px] text-[#24303A] outline-none"
                placeholder="Full Name"
              />
            </div>
            <div className="space-y-1 text-[16px]">
              <label className="block text-[16px] font-medium text-[#24303A]">
                Email
              </label>
              <input
                value={email}
                readOnly
                className="w-full cursor-not-allowed border border-slate-300 rounded-full bg-slate-100 px-3 py-2 text-[16px] text-[#24303A] outline-none"
                placeholder="Email"
              />
            </div>
            <div className="space-y-1 text-[16px] md:col-span-2">
              <label className="block text-[16px] font-medium text-[#24303A]">
                Address
              </label>
              <input
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
                className="w-full rounded-full border border-slate-300 bg-white px-3 py-2 text-[16px] text-[#24303A] outline-none"
                placeholder="Enter full address"
              />
            </div>

          </div>
        </div>
      </div>

      <div className="mt-8 space-y-2">
        {saveError && (
          <p className="text-[16px] text-red-600">{saveError}</p>
        )}
        <button
          type="button"
          onClick={handleSave}
          disabled={saveLoading}
          className="rounded-full bg-[#7352C7] px-8 py-2 text-[16px] w-[120px] font-semibold text-white hover:bg-[#7352C7]-hover disabled:opacity-60"
        >
          {saveLoading ? "Saving…" : "Save"}
        </button>
      </div>

      {showDeleteImageConfirm && (
        <div className="fixed inset-0 z-50">
          <button
            type="button"
            aria-label="Close delete confirmation"
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowDeleteImageConfirm(false)}
          />
          <div className="relative mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-10">
            <div className="relative w-full max-w-md rounded-2xl border border-[#E6EAF0] bg-white p-6 shadow-xl">
              <h3 className="text-lg font-semibold text-slate-900">Delete Picture?</h3>
              <p className="mt-2 text-[16px] text-slate-600">
                Are you sure you want to delete your profile picture?
              </p>
              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowDeleteImageConfirm(false)}
                  className="flex-1 rounded-full border border-slate-300 bg-white px-4 py-2 text-[16px] font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={imageLoading}
                  onClick={async () => {
                    await handleDeleteImage();
                    setShowDeleteImageConfirm(false);
                  }}
                  className="flex-1 rounded-full bg-[#FF3C3F] px-4 py-2 text-[16px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {imageLoading ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PanelShell>
  );
}

function BillingPlansPanel() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const plans = useAppSelector((s) => s.plans.items);
  const status = useAppSelector((s) => s.plans.status);
  const subscriptionState = useAppSelector((s) => s.billing?.subscription);
  const billingStatus = useAppSelector((s) => s.billing?.status);
  const billingError = useAppSelector((s) => s.billing?.error);
  const token = useAppSelector((s) => s.auth.token);
  const [paymentsStatus, setPaymentsStatus] = useState("idle"); // idle | loading | succeeded | failed
  const [paymentsError, setPaymentsError] = useState("");
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    if (status === "idle") dispatch(fetchPlans());
  }, [status, dispatch]);

  useEffect(() => {
    if (!token) return;
    dispatch(fetchCurrentSubscription({ force: true }));
  }, [token, dispatch]);

  useEffect(() => {
    if (!token) return;
    let isActive = true;

    async function loadPayments() {
      setPaymentsStatus("loading");
      setPaymentsError("");
      try {
        const res = await apiRequest("api/billing/payments", {
          method: "GET",
        });

        if (!isActive) return;
        if (!res.ok) {
          const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
          throw new Error(
            errors?.join("\n") || res.data?.message || "Failed to load payments."
          );
        }

        const listRaw =
          Array.isArray(res.data?.payments)
            ? res.data.payments
            : Array.isArray(res.data?.results)
              ? res.data.results
              : Array.isArray(res.data?.data)
                ? res.data.data
                : Array.isArray(res.data)
                  ? res.data
                  : [];

        if (!isActive) return;
        setPayments(Array.isArray(listRaw) ? listRaw : []);
        setPaymentsStatus("succeeded");
      } catch (err) {
        if (!isActive) return;
        setPaymentsStatus("failed");
        setPaymentsError(
          typeof err?.message === "string" && err.message
            ? err.message
            : "Failed to load payments."
        );
      }
    }

    loadPayments();
    return () => {
      isActive = false;
    };
  }, [token]);

  function formatPlanPrice(amountCents) {
    const cents = Number(amountCents ?? 0);
    if (Number.isNaN(cents)) return "£0";
    const amount = cents / 100;
    if (amount === 0) return "Free";
    return `£${amount.toFixed(2)}`;
  }

  function formatPaymentAmount(payment) {
    const currency =
      String(payment?.currency || payment?.currency_code || "GBP").toUpperCase();
    const symbol = currency === "GBP" ? "£" : currency === "EUR" ? "€" : currency === "USD" ? "$" : "";
    const centsCandidate =
      payment?.amount_cents ??
      null;

    if (centsCandidate != null && centsCandidate !== "") {
      const cents = Number(centsCandidate);
      if (Number.isFinite(cents)) {
        const value = cents / 100;
        const formatted = `${symbol || ""}${value.toFixed(2)}`;
        return symbol ? formatted : `${formatted} ${currency}`.trim();
      }
    }

    return symbol ? `${symbol}0` : "0";
  }

  function formatPaymentDate(payment) {
    const candidate =
      payment?.created_at ??
      null;
    if (!candidate) return "";
    const d = new Date(candidate);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString();
  }

  function paymentTimestamp(payment) {
    const candidate = payment?.created_at ?? null;
    if (!candidate) return null;
    const t = new Date(candidate).getTime();
    return Number.isFinite(t) ? t : null;
  }

  function normalizePaymentStatus(payment) {
    const raw =
      payment?.status ??
      "";
    const s = String(raw || "").trim();
    return s || "—";
  }

  function paymentStatusTone(statusText) {
    const s = String(statusText || "").toLowerCase();
    if (s.includes("paid") || s.includes("succeed") || s.includes("success")) return "text-emerald-500";
    if (s.includes("pending") || s.includes("processing")) return "text-amber-500";
    if (s.includes("fail") || s.includes("cancel") || s.includes("error")) return "text-red-500";
    return "text-slate-500";
  }

  function paymentReference(payment) {
    return (
      payment?.id ||
      "—"
    );
  }

  function paymentProduct(payment) {
    return (
      payment?.subscription?.plan.name ||
      "—"
    );
  }

  function isCurrentPlan(subscriptionPlan, plan) {
    if (!subscriptionPlan) return false;
    const sp = String(subscriptionPlan).toLowerCase();

    const candidates = [plan?.id, plan?.name, plan?.plan_title]
      .filter((v) => v !== undefined && v !== null)
      .map((v) => String(v).toLowerCase());

    return candidates.some((c) => c === sp || c.includes(sp) || sp.includes(c));
  }

  function isFreeOrTrialPlan(plan) {
    if (!plan) return false;
    if (plan.is_trial) return true;
    const cents = Number(plan.amount_cents ?? 0);
    return !Number.isNaN(cents) && cents <= 0;
  }

  const currentPlanObj =
    Array.isArray(plans) && subscriptionState?.plan
      ? plans.find((p) => isCurrentPlan(subscriptionState.plan, p))
      : null;

  function formatDateMaybe(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString();
  }

  const currentPlanName =
    currentPlanObj?.name ||
    currentPlanObj?.plan_title ||
    subscriptionState?.plan ||
    "Plan";

  const currentInterval = currentPlanObj?.interval || "";
  const currentIntervalLabel =
    currentInterval === "day"
      ? "Daily"
      : currentInterval === "month"
        ? "Monthly"
        : currentInterval
          ? String(currentInterval)
          : "";

  const isFreeOrTrialCurrent = isFreeOrTrialPlan(currentPlanObj);
  const nextPaymentDate = formatDateMaybe(subscriptionState?.current_period_end);
  const currentAmountLabel =
    currentPlanObj && typeof currentPlanObj?.amount_cents !== "undefined"
      ? formatPlanPrice(currentPlanObj.amount_cents)
      : "";

  const currentPlanLine = (() => {
    if (billingStatus === "loading") return "Loading subscription…";
    if (billingStatus === "failed") return billingError || "Failed to load subscription.";

    if (!subscriptionState) return "No active subscription found.";

    const left =
      currentIntervalLabel
        ? `Billing ${currentIntervalLabel}`
        : subscriptionState?.status
          ? `${subscriptionState.status}`
          : "No Active Subscription";

    if (subscriptionState?.cancel_at_period_end) {
      if (nextPaymentDate) return `Cancelled | Active until ${nextPaymentDate}`;
      return "Cancelled | Will not renew";
    }

    if (isFreeOrTrialCurrent) {
      if (nextPaymentDate) return `${left} | Ends on ${nextPaymentDate}`;
      return `${left} | No renewal`;
    }

    if (nextPaymentDate && currentAmountLabel) {
      return `${left} | Next Payment on ${nextPaymentDate} for ${currentAmountLabel}`;
    }

    if (nextPaymentDate) return `${left} | Next Payment on ${nextPaymentDate}`;
    if (currentAmountLabel) return `${left} | Amount ${currentAmountLabel}`;
    return left;
  })();

  return (
    <PanelShell
      title="Plan & Billing"
      desc="Manage your subscription, review plan details, and keep your account active here."
      action={
        <></>
      }
    >
      <div className="space-y-6 text-[16px] text-slate-600">
        <div className="flex flex-col gap-3 rounded-xl border border-[#E6EAF0] bg-white px-4 py-4 sm:px-5 sm:py-5 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-[18px] font-semibold text-slate-700">
              {currentPlanName}
            </p>
            <p className="mt-1 text-[16px] text-[#2FC98D] capitalize">{currentPlanLine}</p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/subscription")}
            className="w-full rounded-full bg-[#7352C7] px-6 py-2 text-[16px] font-semibold text-white hover:bg-[#7352C7]-hover disabled:opacity-60 md:w-auto"
          >
            Change Plan
          </button>
        </div>

         <div>
          <p className="mb-3 text-[20px] font-semibold text-[#24303A]">
            Transaction history
          </p>
          <div className="hidden overflow-hidden rounded-xl border border-[#E6EAF0] text-[16px] md:block">
            <div className="grid grid-cols-5 bg-slate-50 px-4 py-2 font-medium text-[#6E7B86]">
              <span>Reference</span>
              <span>Product</span>
              <span>Status</span>
              <span>Date</span>
              <span className="text-right">Amount</span>
            </div>
            {paymentsStatus === "loading" ? (
              <div className="border-t border-slate-100 px-4 py-6 text-center text-slate-500">
                Loading…
              </div>
            ) : paymentsStatus === "failed" ? (
              <div className="border-t border-slate-100 px-4 py-6 text-center text-red-600 whitespace-pre-line">
                {paymentsError || "Failed to load payments."}
              </div>
            ) : !payments || payments.length === 0 ? (
              <div className="border-t border-slate-100 px-4 py-6 text-center text-slate-500">
                No transactions yet.
              </div>
            ) : (
              (payments || [])
                .slice()
                .sort(
                  (a, b) =>
                    (paymentTimestamp(b) ?? -Infinity) - (paymentTimestamp(a) ?? -Infinity)
                )
                .slice(0, 5)
                .map((p, idx) => {
                const ref = paymentReference(p);
                const prod = paymentProduct(p);
                const st = normalizePaymentStatus(p);
                const dt = formatPaymentDate(p);
                const amt = formatPaymentAmount(p);
                return (
                  <div
                    key={String(p?.id ?? p?.reference ?? idx)}
                    className="grid grid-cols-5 items-center border-t border-slate-100 px-4 py-2 text-[#24303A]"
                  >
                    <span className="truncate pr-2" title={String(ref)}>
                      {String(ref)}
                    </span>
                    <span className="truncate pr-2" title={String(prod)}>
                      {String(prod)}
                    </span>
                    <span className={paymentStatusTone(st)}>{String(st)}</span>
                    <span>{dt || "—"}</span>
                    <span className="text-right">{amt}</span>
                  </div>
                );
              })
            )}
          </div>

          <div className="space-y-3 md:hidden">
            {paymentsStatus === "loading" ? (
              <div className="rounded-xl border border-[#E6EAF0] bg-white px-4 py-6 text-center text-slate-500">
                Loading…
              </div>
            ) : paymentsStatus === "failed" ? (
              <div className="rounded-xl border border-[#E6EAF0] bg-white px-4 py-6 text-center text-red-600 whitespace-pre-line">
                {paymentsError || "Failed to load payments."}
              </div>
            ) : !payments || payments.length === 0 ? (
              <div className="rounded-xl border border-[#E6EAF0] bg-white px-4 py-6 text-center text-slate-500">
                No transactions yet.
              </div>
            ) : (
              (payments || [])
                .slice()
                .sort(
                  (a, b) =>
                    (paymentTimestamp(b) ?? -Infinity) - (paymentTimestamp(a) ?? -Infinity)
                )
                .slice(0, 5)
                .map((p, idx) => {
                  const ref = paymentReference(p);
                  const prod = paymentProduct(p);
                  const st = normalizePaymentStatus(p);
                  const dt = formatPaymentDate(p);
                  const amt = formatPaymentAmount(p);
                  return (
                    <div
                      key={`mobile-${String(p?.id ?? p?.reference ?? idx)}`}
                      className="rounded-xl border border-[#E6EAF0] bg-white p-4"
                    >
                      <p className="text-[12px] font-medium text-[#6E7B86]">Reference</p>
                      <p className="truncate text-[14px] font-semibold text-[#24303A]" title={String(ref)}>
                        {String(ref)}
                      </p>
                      <p className="mt-2 text-[12px] font-medium text-[#6E7B86]">Product</p>
                      <p className="truncate text-[14px] font-semibold text-[#24303A]" title={String(prod)}>
                        {String(prod)}
                      </p>
                      <div className="mt-3 flex items-center justify-between">
                        <div>
                          <p className="text-[12px] font-medium text-[#6E7B86]">Status</p>
                          <p className={`text-[14px] font-semibold ${paymentStatusTone(st)}`}>{String(st)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[12px] font-medium text-[#6E7B86]">Amount</p>
                          <p className="text-[14px] font-semibold text-[#24303A]">{amt}</p>
                        </div>
                      </div>
                      <p className="mt-2 text-[12px] font-medium text-[#6E7B86]">Date</p>
                      <p className="text-[14px] text-[#24303A]">{dt || "—"}</p>
                    </div>
                  );
                })
            )}
          </div>
        </div> 
      </div>
    </PanelShell>
  );
}

function GrocerySettingsPanel({ view, previousView, onChangeView, selectedId, onSelectId, onBackDetail }) {
  if (view === "detail") {
    return (
      <GroceryDetail
        id={selectedId}
        onBack={() => {
          onSelectId?.(null);
          if (typeof onBackDetail === "function") {
            onBackDetail();
            return;
          }
          onChangeView(previousView === "detail" ? "lists" : previousView || "lists");
        }}
      />
    );
  }

  if (view === "create") {
    return (
      <GroceryListsWithCreate
        onCancel={() => onChangeView("lists")}
      />
    );
  }

  return (
    <GroceryListsOverview
      onOpenCreate={() => onChangeView("create")}
      onOpenDetail={(list) => {
        const id = list?.id ?? null;
        onSelectId?.(id);
        onChangeView("detail", id);
      }}
    />
  );
}

function GroceryListsContent({ lists, status, error, onOpenDetail }) {
  if (status === "loading") {
    return (
      <div className="flex min-h-[360px] items-center justify-center py-10">
        <span className="text-[16px] text-slate-500">Loading…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[360px] items-center justify-center py-6 text-center">
        <p className="text-[16px] text-red-600 whitespace-pre-line">{error}</p>
      </div>
    );
  }

  if (!lists || lists.length === 0) {
    return (
      <div className="flex min-h-[360px] items-center justify-center py-10 text-center text-[16px] text-slate-500">
       No grocery lists yet. Start by creating a list or pasting a full shopping list.
      </div>
    );
  }

  return (
    <div className="mt-3 grid min-h-[200px] grid-cols-1 items-start gap-3 min-[480px]:grid-cols-2 md:mt-4 md:grid-cols-3 lg:grid-cols-4 md:gap-4">
      {lists.map((list) => (
        <button
          key={list.id}
          type="button"
          onClick={() => onOpenDetail?.(list)}
          className="group flex w-full items-start gap-3 rounded-2xl border border-[#E6EAF0] bg-white p-3.5 text-left shadow-sm transition hover:border-[#7352C7]/40 hover:shadow-md sm:p-4"
        >
          <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#7352C7] sm:h-12 sm:w-12">
            <GroceryListStructureIcon className="h-8 w-6 text-white transition-transform duration-200 group-hover:scale-105 sm:h-9 sm:w-7" />
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <p className="truncate text-[18px] font-semibold leading-tight text-[#24303A]">
              {list.title || "N/A"}
            </p>
            <p className="truncate text-[16px] text-[#6E7B86]">
              Budget{" "}
              <span className="font-semibold text-[#7352C7]">{formatGbp(list.budget)}</span>
            </p>
            <p className="text-[16px] text-[#6E7B86]">
              Products:{" "}
              <span className="font-semibold text-[#7352C7]">
                {list.total_products_count ? list.total_products_count : 0}
              </span>
            </p>
          </div>
        </button>
      ))}
    </div>
  );
}

function GroceryListsOverview({ onOpenCreate, onOpenDetail }) {
  const dispatch = useAppDispatch();
  const lists = useAppSelector((s) => s.groceryLists.items);
  const status = useAppSelector((s) => s.groceryLists.fetchStatus);
  const error = useAppSelector((s) => s.groceryLists.fetchError);

  useEffect(() => {
    if (status === "idle") dispatch(fetchGroceryLists());
  }, [status, dispatch]);

  return (
    <PanelShell
      title="My Grocery Lists"
      desc="Create and manage lists for weekly shops, meal plans, or different households."
      action={
        <button
          className="rounded-full bg-[#7352C7] px-5 py-2 text-[16px] font-bold text-white hover:bg-[#7352C7]-hover"
          onClick={onOpenCreate}
        >
          Create List
        </button>
      }
    >
      <GroceryListsContent
        lists={lists}
        status={status}
        error={error}
        onOpenDetail={onOpenDetail}
      />
    </PanelShell>
  );
}

function GroceryListsWithCreate({ onCancel }) {
  const dispatch = useAppDispatch();
  const createStatus = useAppSelector((s) => s.groceryLists.createStatus);
  const createError = useAppSelector((s) => s.groceryLists.createError);
  const [title, setTitle] = useState("");
  const [budget, setBudget] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    dispatch(clearCreateGroceryListState());
    setTitle("");
    setBudget("");
    setFieldErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function validate() {
    const errs = {};
    if (!title.trim()) errs.title = "Title is required.";
    const parsedBudget = parseBudgetAmount(budget);
    if (parsedBudget === "" || parsedBudget == null) {
      errs.budget = parsedBudget == null ? "Budget must be a number." : "Budget must be greater than 0.";
    } else if (parsedBudget <= 0) {
      errs.budget = "Budget must be greater than 0.";
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleCreate() {
    if (!validate()) return;
    const parsedBudget = parseBudgetAmount(budget);
    const payload = {
      title: title.trim(),
      budget: parsedBudget === "" ? undefined : parsedBudget,
    };
    try {
      await dispatch(createGroceryList(payload)).unwrap();
      // Ensure list is fresh even if API doesn't return object
      dispatch(fetchGroceryLists({ force: true }));
      onCancel?.(); // close popup back to lists view
    } catch (e) {
      // error shown from createError
    }
  }

  return (
    <PanelShell
      title="My Grocery Lists"
      action={
        <button
          type="button"
          className="rounded-full bg-[#7352C7] px-5 py-2 text-[16px] font-semibold text-white hover:bg-[#7352C7]-hover"
        >
          + Create New List
        </button>
      }
    >
      <div className="fixed inset-0 z-50">
        <button
          type="button"
          aria-label="Close create list popup"
          className="absolute inset-0 bg-black/40"
          onClick={onCancel}
        />
        <div className="relative mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-10">
          <div className="relative w-full max-w-md rounded-3xl border border-[#E6EAF0] bg-white shadow-2xl">
            <div className="relative flex items-center justify-center px-6 pt-6">
              <p className="text-[24px] md:text-[24px] font-semibold text-femty-text">
                Create List
              </p>
              <button
                type="button"
                onClick={onCancel}
                className="absolute right-6 inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#E6EAF0] bg-white p-0 text-slate-500 hover:bg-[#7352C7]/10 hover:text-femty-primary"
              >
                ✕
              </button>
            </div>

            <div className="px-6 pb-6 pt-4">
              {!!createError && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[16px] text-red-700 whitespace-pre-line">
                  {createError}
                </div>
              )}

              <div className="space-y-5">
                <div className="space-y-1">
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full rounded-full border border-[#E6EAF0] bg-white px-4 py-2 text-[16px] font-normal text-femty-text shadow-sm focus:border-femty-primary focus:outline-none"
                    placeholder="List title"
                  />
                  {fieldErrors.title && (
                    <p className="mt-1 text-[16px] text-red-600">{fieldErrors.title}</p>
                  )}
                </div>

                <div className="space-y-1">
                  <div className="relative">
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[16px] font-semibold text-[#7352C7]"
                    >
                      £
                    </span>
                    <input
                      value={budget}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^0-9.]/g, "");
                        setBudget(raw);
                      }}
                      onBlur={() => {
                        if (budget === "") return;
                        setBudget(formatBudgetAmountForInput(budget));
                      }}
                      inputMode="decimal"
                      className="w-full rounded-full border border-[#E6EAF0] bg-white py-2 pl-8 pr-4 text-[16px] font-normal text-femty-text shadow-sm focus:border-femty-primary focus:outline-none"
                      placeholder="0.00"
                      aria-label="Budget in pounds"
                    />
                  </div>
                  {fieldErrors.budget && (
                    <p className="mt-1 text-[16px] text-red-600">{fieldErrors.budget}</p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={createStatus === "loading"}
                  className="w-full rounded-full bg-femty-primary py-2 text-[16px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {createStatus === "loading" ? "Saving..." : "Create List"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PanelShell>
  );
}

function GroceryDetail({ id, onBack }) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const detail = useAppSelector((s) => s.groceryLists.detail);
  const detailStatus = useAppSelector((s) => s.groceryLists.detailStatus);
  const detailError = useAppSelector((s) => s.groceryLists.detailError);
  const updateStatus = useAppSelector((s) => s.groceryLists.updateStatus);
  const updateError = useAppSelector((s) => s.groceryLists.updateError);
  const deleteStatus = useAppSelector((s) => s.groceryLists.deleteStatus);
  const deleteError = useAppSelector((s) => s.groceryLists.deleteError);
  const addProductStatus = useAppSelector((s) => s.groceryLists.addProductStatus);
  const addProductError = useAppSelector((s) => s.groceryLists.addProductError);
  const updateProductStatus = useAppSelector((s) => s.groceryLists.updateProductStatus);
  const updateProductError = useAppSelector((s) => s.groceryLists.updateProductError);
  const deleteProductStatus = useAppSelector((s) => s.groceryLists.deleteProductStatus);
  const deleteProductError = useAppSelector((s) => s.groceryLists.deleteProductError);
  const token = useAppSelector((s) => s.auth.token);
  const user = useAppSelector((s) => s.auth.user);
  const authStatus = useAppSelector((s) => s.auth.status);
  const [renameOpen, setRenameOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmDeleteProduct, setConfirmDeleteProduct] = useState(null);
  const [addProductOpen, setAddProductOpen] = useState(false);
  const [productId, setProductId] = useState("");
  const [productName, setProductName] = useState("");
  const [productPlatform, setProductPlatform] = useState("tesco");
  const [productCategory, setProductCategory] = useState("");
  const [productImageUrl, setProductImageUrl] = useState("");
  const [productPrice, setProductPrice] = useState("");
  const [productWeight, setProductWeight] = useState("");
  const [productQty, setProductQty] = useState(1);
  const [title, setTitle] = useState("");
  const [budget, setBudget] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [compareConfirmOpen, setCompareConfirmOpen] = useState(false);
  const [compareSubmitting, setCompareSubmitting] = useState(false);
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const [limitModalMessage, setLimitModalMessage] = useState("");
  // Keep a stable ordering for the cards while user updates quantities.
  // Refetching the list can reorder items (e.g., by lowest price), which feels wrong UX.
  const [orderedProducts, setOrderedProducts] = useState([]);
  const lastInitializedDetailIdRef = useRef(null);

  const normalizedToken = typeof token === "string" ? token.trim() : token;
  const isLoggedIn =
    Boolean(normalizedToken) &&
    normalizedToken !== "null" &&
    normalizedToken !== "undefined";
  const profileLoading = isLoggedIn && !user && authStatus === "loading";
  const canStartComparison = user?.allowed === true && user?.can_compare === true;
  const comparisonLimitMessage =
    "Monthly basket comparison limit reached. Try again next month or upgrade your plan.";
  const isBasketSummaryDisabled = isLoggedIn && Boolean(user) && !canStartComparison;
  const comparisonUsage = useMemo(() => getBasketComparisonUsage(user), [user]);

  useEffect(() => {
    if (!isLoggedIn || user || authStatus === "loading") return;
    dispatch(fetchCurrentUser());
  }, [isLoggedIn, user, authStatus, dispatch]);

  useEffect(() => {
    dispatch(clearUpdateGroceryListState());
    dispatch(clearDeleteGroceryListState());
    dispatch(clearAddGroceryProductState());
    dispatch(clearUpdateGroceryProductState());
    dispatch(clearDeleteGroceryProductState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!id) return;
    dispatch(fetchGroceryListDetail({ id, force: true }));
  }, [id, dispatch]);

  useEffect(() => {
    if (!detail || !sameGroceryListId(detail.id ?? detail.grocery_list_id, id)) return;
    const values = groceryListFormValues(detail);
    setTitle(values.title);
    setBudget(values.budget);
  }, [detail, id]);

  function validateRename() {
    const errs = {};
    if (!title.trim()) errs.title = "Title is required.";
    const parsedBudget = parseBudgetAmount(budget);
    if (parsedBudget === "" || parsedBudget == null) {
      errs.budget = parsedBudget == null ? "Budget must be a number." : "Budget must be greater than 0.";
    } else if (parsedBudget <= 0) {
      errs.budget = "Budget must be greater than 0.";
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleUpdate() {
    if (!id) return;
    if (!validateRename()) return;
    const parsedBudget = parseBudgetAmount(budget);
    const payload = {
      id,
      title: title.trim(),
      budget: parsedBudget === "" ? undefined : parsedBudget,
    };
    try {
      await dispatch(updateGroceryList(payload)).unwrap();
      dispatch(fetchGroceryListDetail({ id, force: true }));
      dispatch(fetchGroceryLists({ force: true }));
      setRenameOpen(false);
    } catch (e) {
      // shown in updateError
    }
  }

  async function handleDelete() {
    if (!id) return;
    try {
      await dispatch(deleteGroceryList({ id })).unwrap();
      dispatch(fetchGroceryLists({ force: true }));
      setConfirmDeleteOpen(false);
      onBack?.();
    } catch (e) {
      // shown in deleteError
    }
  }

  async function handleDeleteProduct() {
    const pid = confirmDeleteProduct?.id;
    if (!id || pid == null) return;
    try {
      await dispatch(
        deleteGroceryProduct({ grocery_list_id: id, id: pid })
      ).unwrap();
      setOrderedProducts((prev) =>
        (prev || []).filter((item) => item?.id !== pid)
      );
      dispatch(fetchGroceryListDetail({ id, force: true }));
      dispatch(fetchGroceryLists({ force: true }));
      setConfirmDeleteProduct(null);
    } catch (e) {
      // shown via deleteProductError
    }
  }

  const products =
    detail?.products ||
    detail?.items ||
    detail?.grocery_list_products ||
    detail?.grocery_products ||
    [];
  // Initialize stable ordering once per grocery list id.
  useEffect(() => {
    if (!id) return;
    if (detailStatus === "loading") return;
    if (!detail || detail?.id !== id) return;
    if (lastInitializedDetailIdRef.current === id) return;
    lastInitializedDetailIdRef.current = id;
    setOrderedProducts(sortGroceryProductsByCreatedAt(products));
  }, [detailStatus, detail?.id, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayedProducts =
    Array.isArray(orderedProducts) && orderedProducts.length > 0 ? orderedProducts : products;
  const isInitialLoading = detailStatus === "loading" && !detail;

  const parseMoney = (value) => {
    if (value == null) return 0;
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    const cleaned = String(value).replace(/[^0-9.]/g, "");
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : 0;
  };

  const budgetValue = parseMoney(detail?.budget);
  const totalValue = parseMoney(detail?.total_price ?? detail?.total);
  const rawBudgetPercent = budgetValue > 0 ? Math.round((totalValue / budgetValue) * 100) : 0;
  const budgetPercent = Math.max(0, Math.min(100, rawBudgetPercent));

  const bannerTone =
    rawBudgetPercent > 100 ? "danger" : rawBudgetPercent >= 80 ? "warning" : "success";

  const toneStyles = {
    success: {
      bg: "#F6F7FB",
      bar: "#2FC98D",
      track: "#DDE3EA",
      subtext: "#2FC98D",
    },
    warning: {
      bg: "#F6F7FB",
      bar: "#F59E0B",
      track: "#DDE3EA",
      subtext: "#F59E0B",
    },
    danger: {
      bg: "#F6F7FB",
      bar: "#FF3C3F",
      track: "#DDE3EA",
      subtext: "#FF3C3F",
    },
  };

  const tone = toneStyles[bannerTone];
  const bannerSubtitle =
    typeof detail?.budget_message === "string" && detail.budget_message.trim().length > 0
      ? detail.budget_message.trim()
      : rawBudgetPercent > 100
        ? "Over budget! Try swapping items for cheaper options."
        : "Compare Prices Across Retailers, Then Add The Best Deals To Your Grocery List.";

  const openLimitModal = useCallback((message) => {
    setLimitModalMessage(message || comparisonLimitMessage);
    setLimitModalOpen(true);
  }, [comparisonLimitMessage]);

  const proceedToCheapestBasket = useCallback(async () => {
    const productsForBasket = Array.isArray(displayedProducts) ? displayedProducts : [];
    if (!productsForBasket.length) {
      showToast({
        type: "error",
        title: "Empty list",
        message: "Add products to this grocery list before comparing baskets.",
      });
      return false;
    }
    if (!id) {
      showToast({
        type: "error",
        title: "List unavailable",
        message: "Grocery list id is missing.",
      });
      return false;
    }

    const { basketTotals, basketComparison } = await fetchGroceryBasketSummary(id);
    if (basketComparison) {
      dispatch(updateBasketComparisonUsage(basketComparison));
    }

    if (!basketTotals?.rows?.length) {
      showToast({
        type: "error",
        title: "No basket totals",
        message: "Could not calculate store totals for this grocery list yet.",
      });
      return false;
    }

    savePasteListFlow({
      source: "grocery_list",
      groceryListId: id,
      products: productsForBasket,
      basketTotals,
      totalCount: productsForBasket.length,
      itemCount: basketTotals.rows?.length ? productsForBasket.length : 0,
      lines: productsForBasket
        .map((p) => p?.product_name || p?.title || p?.name || "")
        .filter(Boolean),
    });
    navigate("/paste-list/summary", {
      state: { source: "grocery_list", fromGrocery: true, groceryListId: id },
    });
    return true;
  }, [displayedProducts, id, navigate, showToast, dispatch]);

  const handleSeeCheapestBasket = useCallback(async () => {
    if (profileLoading) return;
    if (!user) {
      dispatch(fetchCurrentUser());
      return;
    }
    if (!canStartComparison) {
      openLimitModal(comparisonLimitMessage);
      return;
    }

    let latestProfile = user;
    try {
      latestProfile = await dispatch(fetchCurrentUser({ force: true })).unwrap();
    } catch {
      // Use cached profile if refresh fails.
    }

    const canCompareNow =
      latestProfile?.allowed === true && latestProfile?.can_compare === true;
    if (!canCompareNow) {
      openLimitModal(comparisonLimitMessage);
      return;
    }

    setCompareConfirmOpen(true);
  }, [
    profileLoading,
    user,
    canStartComparison,
    comparisonLimitMessage,
    dispatch,
    openLimitModal,
  ]);

  const handleConfirmCompare = useCallback(async () => {
    setCompareSubmitting(true);
    try {
      // calculate_basket_summary both computes rankings and returns basket_comparison usage.
      await proceedToCheapestBasket();
      setCompareConfirmOpen(false);
    } catch (err) {
      const message =
        err?.data?.errors?.[0] ||
        err?.message ||
        "Failed to calculate basket summary.";
      if (err?.data?.basket_comparison) {
        dispatch(updateBasketComparisonUsage(err.data.basket_comparison));
      } else if (err?.data) {
        dispatch(updateBasketComparisonUsage(err.data));
      }
      setCompareConfirmOpen(false);
      if (
        /limit|compar/i.test(String(message)) ||
        err?.status === 403 ||
        err?.data?.can_compare === false ||
        err?.data?.allowed === false
      ) {
        openLimitModal(message);
      } else {
        showToast({
          type: "error",
          title: "Basket summary failed",
          message,
        });
      }
    } finally {
      setCompareSubmitting(false);
    }
  }, [dispatch, openLimitModal, proceedToCheapestBasket, showToast]);

  return (
    <>
      <div className="mb-2 px-1 sm:px-0">
        <button
          type="button"
          onClick={() => onBack?.()}
          className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#E6EAF0] bg-white px-4 py-2 text-[14px] font-bold text-[#24303A] shadow-sm sm:text-[16px]"
        >
          <span className="leading-none">Back</span>
        </button>
      </div>
      <div
        className="mb-2 rounded-2xl border-2 border-[#E6EAF0] bg-[#2FC98D0D] px-3 py-3 shadow-md sm:px-6 sm:py-2"
      >
        <p className="text-[20px] font-semibold text-[#24303A]">Basket vs Budget</p>

        <div className="mt-2 flex flex-col gap-4 md:flex-row md:items-end md:justify-between md:gap-6">
          <p className="min-w-0 flex-1 text-[16px] font-normal" style={{ color: tone.subtext }}>
            {bannerSubtitle}
          </p>

          <div className="flex w-full max-w-full flex-col gap-2 md:w-[420px] md:max-w-[45%] md:items-end">
            <div className="relative w-full pt-5" dir="ltr">
              <div
                className="absolute top-0 -translate-x-1/2 rounded-[6px] bg-white/70 px-2 py-0.5 text-[12px] font-normal text-[#24303A]"
                style={{
                  left: `clamp(14px, ${isInitialLoading ? 0 : budgetPercent}%, calc(100% - 14px))`,
                }}
              >
                {isInitialLoading ? "—" : `${Math.max(0, rawBudgetPercent)}%`}
              </div>

              <div
                className="h-[8px] w-full rounded-full overflow-hidden"
                style={{ backgroundColor: tone.track }}
              >
                <div
                  className="h-[8px] w-full rounded-full transition-transform duration-300 origin-left"
                  style={{
                    backgroundColor: tone.bar,
                    transform: `scaleX(${isInitialLoading ? 0 : budgetPercent / 100})`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mb-2 flex flex-col items-start gap-2 px-2 font-semibold text-[rgb(176,48,28)] text-[18px] sm:flex-row sm:items-center sm:gap-2 sm:px-4">
        <span className="relative inline-flex shrink-0 group" aria-label="Basket totals use standard prices">
          <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 10v6" strokeLinecap="round" />
            <circle cx="12" cy="7" r="1" fill="currentColor" stroke="none" />
          </svg>
        </span>
        <span className="min-w-0">Basket totals currently use standard prices. Loyalty basket calculations are coming soon.</span>
      </div>
      <PanelShell
        title={detail?.title || "Grocery Detail"}
        className="!pt-7 sm:!pt-8"
        action={
          <div className="flex w-full min-w-0 flex-col gap-1.5 sm:items-end">
            <div className="w-full min-w-0 overflow-x-auto overscroll-x-contain px-0.5 py-1.5 sm:overflow-visible sm:px-0 sm:py-0">
              <div className="flex w-max items-start gap-2 pr-1 sm:ml-auto sm:flex-wrap sm:justify-end sm:pr-0">
                <button
                  type="button"
                  onClick={() => {
                    navigate("/");
                  }}
                  className="shrink-0 whitespace-nowrap rounded-full border border-[#E6EAF0] bg-[#7352C7] px-4 py-1.5 text-[14px] font-bold text-white hover:bg-[#7352C7]-hover sm:px-5 sm:text-[16px]"
                >
                  Add to list
                </button>
                <div
                  className={`relative flex shrink-0 flex-col${
                    isBasketSummaryDisabled ? " cursor-not-allowed p-0.5" : ""
                  }`}
                >
                  <button
                    type="button"
                    onClick={handleSeeCheapestBasket}
                    disabled={
                      isBasketSummaryDisabled ||
                      profileLoading ||
                      isInitialLoading ||
                      !displayedProducts.length
                    }
                    aria-describedby={
                      isBasketSummaryDisabled &&
                      isLoggedIn &&
                      user &&
                      comparisonUsage &&
                      !comparisonUsage.unlimited
                        ? "grocery-basket-summary-comparison-status"
                        : undefined
                    }
                    aria-label={
                      isBasketSummaryDisabled
                        ? `See Cheapest Basket — ${comparisonLimitMessage}`
                        : "See Cheapest Basket"
                    }
                    className={`shrink-0 whitespace-nowrap rounded-full bg-[#0d9499] px-4 py-1.5 text-[14px] font-bold text-white shadow-sm disabled:cursor-not-allowed sm:px-5 sm:text-[16px]${
                      isBasketSummaryDisabled
                        ? " bg-[#0d9499]/45 text-white/85 ring-2 ring-[#C62828] ring-offset-1"
                        : " disabled:opacity-60"
                    }`}
                  >
                    See Cheapest Basket
                  </button>
                  {isBasketSummaryDisabled &&
                    isLoggedIn &&
                    user &&
                    comparisonUsage &&
                    !comparisonUsage.unlimited && (
                      <div className="relative mt-3 w-max max-w-[220px]" role="alert">
                        <span
                          className="absolute -top-1.5 left-6 h-2.5 w-2.5 rotate-45 bg-[#C62828]"
                          aria-hidden="true"
                        />
                        <p
                          id="grocery-basket-summary-comparison-status"
                          className="rounded-md bg-[#C62828] px-3 py-2 text-left text-[13px] font-medium leading-snug text-white shadow-md sm:text-[14px]"
                        >
                          {formatComparisonCount(comparisonUsage.remaining)} comparison
                          {comparisonUsage.remaining === 1 ? "" : "s"} available
                        </p>
                      </div>
                    )}
                  {!isBasketSummaryDisabled &&
                    isLoggedIn &&
                    user &&
                    comparisonUsage &&
                    !comparisonUsage.unlimited && (
                      <p className="mt-1 whitespace-nowrap text-center text-[14px] font-normal text-[#6E7B86] sm:text-[16px]">
                        {formatComparisonCount(comparisonUsage.remaining)} comparison
                        {comparisonUsage.remaining === 1 ? "" : "s"} available
                      </p>
                    )}
                  {!isBasketSummaryDisabled && isLoggedIn && user && comparisonUsage?.unlimited && (
                    <p className="mt-1 whitespace-nowrap text-center text-[16px] font-normal text-[#6E7B86]">
                      Unlimited comparisons available
                    </p>
                  )}
                </div>
                {isBasketSummaryDisabled && (
                  <button
                    type="button"
                    onClick={() => navigate("/subscription")}
                    className="shrink-0 whitespace-nowrap rounded-full border border-[#7352C7] bg-white px-4 py-1.5 text-[14px] font-bold text-[#7352C7] shadow-sm sm:px-5 sm:text-[16px]"
                  >
                    Upgrade plan
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    const values = groceryListFormValues(detail);
                    setTitle(values.title);
                    setBudget(values.budget);
                    setRenameOpen(true);
                    dispatch(clearUpdateGroceryListState());
                    setFieldErrors({});
                  }}
                  className="shrink-0 whitespace-nowrap rounded-full bg-[#7352C71A] px-4 py-1.5 text-[14px] font-bold text-[#7352C7] sm:px-5 sm:text-[16px]"
                >
                  Edit List
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmDeleteOpen(true);
                    dispatch(clearDeleteGroceryListState());
                  }}
                  className="shrink-0 whitespace-nowrap rounded-full bg-[#FF3C3F] px-4 py-1.5 text-[14px] font-bold text-white sm:px-5 sm:text-[16px]"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        }
    >
        {isInitialLoading ? (
        <div className="flex min-h-[280px] items-center justify-center">
          <span className="text-[16px] font-normal text-[#6E7B86]">Loading…</span>
        </div>
      ) : detailError ? (
        <div className="py-6 text-center">
          <p className="text-[16px] text-red-600 whitespace-pre-line">{detailError}</p>
        </div>
        ) : !detail ? (
        <div className="py-10 text-center text-[16px] font-normal text-[#6E7B86]">
          Select a list to view details.
        </div>
      ) : (
        <>
          <div className="mb-4 overflow-hidden rounded-2xl border border-[#E6EAF0] bg-white">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 px-4 py-3 text-[16px] text-[#6E7B86] sm:px-5 sm:py-3.5">
              {detail?.budget != null && String(detail.budget) !== "" ? (
                <p>
                  Budget{" "}
                  <span className="font-semibold text-[#7352C7]">{formatGbp(detail.budget)}</span>
                </p>
              ) : (
                <span aria-hidden="true" />
              )}
              <p className="text-right">
                Products:{" "}
                <span className="font-semibold text-[#7352C7]">
                  {Array.isArray(products) ? products.length : 0}
                </span>
              </p>
            </div>
            {detail?.total_price != null && String(detail.total_price) !== "" ? (
              <div className="border-t border-[#E6EAF0] bg-[#7352C7]/[0.04] px-4 py-3 sm:px-5">
                <p className="text-[16px] text-[#6E7B86]">
                  Total:{" "}
                  <span className="text-[17px] font-semibold text-[#7352C7]">
                    {formatGbp(detail.total_price)}
                  </span>
                </p>
              </div>
            ) : null}
          </div>

              {Array.isArray(products) && products.length > 0 ? (
            <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {displayedProducts.map((p, idx) => (
                <div
                  key={p?.id ?? p?.grocery_product_id ?? p?.product_id ?? idx}
                  className="flex w-full min-w-0 flex-col rounded-2xl border border-[#E6EAF0] bg-white p-3"
                >
                  <div className="relative mb-3 flex h-[160px] items-center justify-center rounded-xl bg-white">
                    {p?.image_url ? (
                      <img
                        src={p.image_url}
                        alt={p?.title || p?.name || p?.product_id || "Product"}
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <div className="flex h-full w-full flex-col items-center justify-center gap-2 rounded-xl border border-[#E6EAF0] bg-slate-50">
              <svg
                width="34"
                height="34"
                viewBox="0 0 24 24"
                fill="none"
                className="text-slate-400"
                aria-hidden
              >
                <path
                  d="M21 16.2V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v11.2"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
                <path
                  d="M3.5 18.5l4.7-4.7a1.5 1.5 0 0 1 2.1 0l2.4 2.4a1.5 1.5 0 0 0 2.1 0l1.2-1.2a1.5 1.5 0 0 1 2.1 0L21 18.2"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10.5 8.5h.01"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
              <div className="text-center">
                <div className="text-[16px] font-semibold text-slate-600">Image unavailable</div>
                <div className="text-[12px] font-medium text-slate-400">We’ll update it soon</div>
              </div>
            </div>
                    )}
                  </div>
                  <p className="line-clamp-3 text-[16px] font-semibold text-[#24303A]">
                    {p?.product_name || "Product"}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
          <div className="flex flex-wrap items-center gap-2">
            {(p?.category || p?.category_name) && (
              <span className="inline-flex rounded-full bg-[#2EB5C9] px-2.5 py-0.5 text-[12px] font-semibold text-[#FFFFFF]">
                {p?.category || p?.category_name}
              </span>
            )}
            {(p.weight || p.packsize) && (
              <span className="inline-flex rounded-full bg-[#F59E0B] px-2.5 py-0.5 text-[12px] font-semibold text-[#FFFFFF]">
                {p.weight || p.packsize}
              </span>
            )}
          </div>
        </div>
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-full border border-[#2FC98D80] bg-[#2FC98D1A] px-4 py-1.5 text-[16px]">
                    <span className="min-w-0 flex-1 truncate capitalize text-[#24303A]">{p?.platform}</span>
                    <span className="whitespace-nowrap text-[16px] font-semibold text-[#7352C7]">
                      {(() => {
                        // Backend returns the line total price (already quantity-adjusted).
                        const price = Number(p?.price);
                        if (!Number.isFinite(price)) return null;
                        return `£${price.toFixed(2)}`;
                      })()}
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={updateProductStatus === "loading"}
                        className="h-10 w-10 flex-shrink-0 flex items-center justify-center rounded-full border-2 border-[#E6EAF0] bg-[#7352C71A] text-xl font-medium text-slate-700"
                        onClick={async () => {
                          const nextQty = Math.max(1, Number(p?.quantity || 1) - 1);
                          const rowId = p?.id ?? p?.grocery_product_id ?? p?.grocery_list_product_id;
                          if (!id || rowId == null) return;
                          try {
                            const res = await dispatch(
                              updateGroceryProduct({
                                grocery_list_id: id,
                                id: rowId,
                                product_id: p.product_id,
                                product_name: p?.title || p?.name || p?.product_name || p?.product_id,
                                platform: p?.platform || undefined,
                                category: p?.category || undefined,
                                image_url: p.image_url,
                                price: p.price,
                                weight: p.weight,
                                quantity: nextQty,
                              })
                            ).unwrap();
                            // Update local state only to keep card position stable.
                            const updated = res?.data && typeof res.data === "object" ? res.data : null;
                            setOrderedProducts((prev) => {
                              const base = prev && prev.length ? prev : Array.isArray(products) ? products : [];
                              return base.map((item) =>
                                (item?.id ?? item?.grocery_product_id ?? item?.grocery_list_product_id) === rowId
                                  ? { ...item, ...(updated || {}), quantity: nextQty }
                                  : item
                              );
                            });
                            // Refresh totals/budget in header without changing card order.
                            dispatch(fetchGroceryListDetail({ id, force: true }));
                          } catch (e) {
                            showToast({
                              type: "error",
                              title: "Quantity Update Failed",
                              message:
                                typeof e === "string"
                                  ? e
                                  : e?.message || "Failed to update quantity.",
                            });
                          }
                        }}
                      >
                        −
                      </button>
                      <span className="min-w-6 text-center text-[16px] font-semibold text-slate-800">
                        {p?.quantity ?? 1}
                      </span>
                      <button
                        type="button"
                        disabled={updateProductStatus === "loading"}
                        className="h-10 w-10 flex-shrink-0 flex items-center justify-center rounded-full border-2 border-[#E6EAF0] bg-[#7352C71A] text-xl font-medium text-slate-700"
                        onClick={async () => {
                          const nextQty = Number(p?.quantity || 1) + 1;
                          const rowId = p?.id ?? p?.grocery_product_id ?? p?.grocery_list_product_id;
                          if (!id || rowId == null) return;
                          try {
                            const res = await dispatch(
                              updateGroceryProduct({
                                grocery_list_id: id,
                                id: rowId,
                                product_id: p.product_id,
                                product_name: p?.title || p?.name || p?.product_name || p?.product_id,
                                platform: p?.platform || undefined,
                                category: p?.category || undefined,
                                image_url: p.image_url,
                                price: p.price,
                                weight: p.weight,
                                quantity: nextQty,
                              })
                            ).unwrap();
                            // Update local state only to keep card position stable.
                            const updated = res?.data && typeof res.data === "object" ? res.data : null;
                            setOrderedProducts((prev) => {
                              const base = prev && prev.length ? prev : Array.isArray(products) ? products : [];
                              return base.map((item) =>
                                (item?.id ?? item?.grocery_product_id ?? item?.grocery_list_product_id) === rowId
                                  ? { ...item, ...(updated || {}), quantity: nextQty }
                                  : item
                              );
                            });
                            // Refresh totals/budget in header without changing card order.
                            dispatch(fetchGroceryListDetail({ id, force: true }));
                          } catch (e) {
                            showToast({
                              type: "error",
                              title: "Quantity Update Failed",
                              message:
                                typeof e === "string"
                                  ? e
                                  : e?.message || "Failed to update quantity.",
                            });
                          }
                        }}
                      >
                        +
                      </button>
                    </div>
                    <div className="flex items-center gap-2 ">
                      <button
                        type="button"
                        className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-[#E6EAF0] bg-white text-slate-700 hover:bg-slate-100"
                        onClick={() => {
                          const nav = getGroceryProductNavigation({ listId: id, item: p });
                          if (nav) {
                            navigate(nav.path, { state: nav.state });
                            return;
                          }
                          showToast({
                            type: "error",
                            title: "Product unavailable",
                            message: "This item does not have a product ID to open.",
                          });
                        }}
                        aria-label="View product"
                        title="View product"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className="h-4 w-4"
                        >
                          <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        disabled={deleteProductStatus === "loading"}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-red-500 text-white disabled:opacity-60"
                        onClick={() => {
                          setConfirmDeleteProduct({
                            id: p.id,
                            name:
                              p?.product_name ||
                              p?.title ||
                              p?.name ||
                              p?.product_id ||
                              "this product",
                          });
                          dispatch(clearDeleteGroceryProductState());
                        }}
                        aria-label="Delete product"
                        title="Delete product"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className="h-4 w-4"
                        >
                          <path d="M3 6h18" />
                          <path d="M8 6V4h8v2" />
                          <path d="M19 6l-1 14H6L5 6" />
                          <path d="M10 11v6M14 11v6" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex min-h-[220px] items-center justify-center py-10 text-center text-[16px] font-normal text-[#6E7B86]">
              No products in this list yet.
            </div>
          )}
        </>
      )}

      {/* Edit list details popup */}
      {renameOpen && (
        <div className="fixed inset-0 z-50">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={() => setRenameOpen(false)}
            aria-label="Close"
          />
          <div className="relative mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-10">
            <div className="relative w-full max-w-md rounded-3xl border border-[#E6EAF0] bg-white shadow-2xl">
              <div className="relative flex items-center justify-center px-6 pt-6">
                <p className="text-[24px] font-semibold text-femty-text">
                  Edit List Details
                </p>
                <button
                  type="button"
                  onClick={() => setRenameOpen(false)}
                  className="absolute right-6 inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#E6EAF0] bg-white p-0 text-slate-500 hover:bg-[#7352C7]/10 hover:text-femty-primary"
                >
                  ✕
                </button>
              </div>

              <div className="px-6 pb-6 pt-4">
              {!!updateError && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[16px] text-red-700 whitespace-pre-line">
                  {updateError}
                </div>
              )}

              <div className="space-y-5">
                <div className="space-y-1">
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full rounded-full border border-[#E6EAF0] bg-white px-4 py-2 text-[16px] font-normal text-femty-text shadow-sm focus:border-femty-primary focus:outline-none"
                    placeholder="List title"
                  />
                  {fieldErrors.title && (
                    <p className="mt-1 text-[16px] text-red-600">{fieldErrors.title}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <div className="relative">
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[16px] font-semibold text-[#7352C7]"
                    >
                      £
                    </span>
                    <input
                      value={budget}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^0-9.]/g, "");
                        setBudget(raw);
                      }}
                      onBlur={() => {
                        if (budget === "") return;
                        setBudget(formatBudgetAmountForInput(budget));
                      }}
                      inputMode="decimal"
                      className="w-full rounded-full border border-[#E6EAF0] bg-white py-2 pl-8 pr-4 text-[16px] font-normal text-femty-text shadow-sm focus:border-femty-primary focus:outline-none"
                      placeholder="0.00"
                      aria-label="Budget in pounds"
                    />
                  </div>
                  {fieldErrors.budget && (
                    <p className="mt-1 text-[16px] text-red-600">{fieldErrors.budget}</p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={handleUpdate}
                  disabled={updateStatus === "loading"}
                  className="w-full rounded-full bg-femty-primary py-2 text-[16px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {updateStatus === "loading" ? "Saving..." : "Save Changes"}
                </button>
              </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm popup */}
      {confirmDeleteOpen && (
        <>
          <div className="fixed inset-0 z-50">
          <button
            type="button"
            aria-label="Close delete confirmation"
            className="absolute inset-0 bg-black/40"
            onClick={() => setConfirmDeleteOpen(false)}
          />
          <div className="relative mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-10">
            <div className="relative w-full max-w-md rounded-2xl border border-[#E6EAF0] bg-white p-6 shadow-xl">
              <h3 className="text-lg font-semibold text-slate-900">Delete List?</h3>
              <p className="mt-2 text-[16px] text-slate-600">
                Are you sure you want to delete this list?
              </p>

              {!!deleteError && (
                <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[16px] text-red-700 whitespace-pre-line">
                  {deleteError}
                </div>
              )}

              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => setConfirmDeleteOpen(false)}
                  className="flex-1 rounded-full border border-slate-300 bg-white px-4 py-2 text-[16px] font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleteStatus === "loading"}
                  className="flex-1 rounded-full bg-[#FF3C3F] px-4 py-2 text-[16px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                     {deleteStatus === "loading" ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
       
        </>
      )}

      {confirmDeleteProduct && (
        <div className="fixed inset-0 z-50">
          <button
            type="button"
            aria-label="Close delete confirmation"
            className="absolute inset-0 bg-black/40"
            onClick={() => setConfirmDeleteProduct(null)}
          />
          <div className="relative mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-10">
            <div className="relative w-full max-w-md rounded-2xl border border-[#E6EAF0] bg-white p-6 shadow-xl">
              <h3 className="text-lg font-semibold text-slate-900">Delete product?</h3>
              <p className="mt-2 text-[16px] text-slate-600">
                Are you sure you want to delete{" "}
                <span className="font-medium text-slate-800">
                  {confirmDeleteProduct.name}
                </span>
                ?
              </p>

              {!!deleteProductError && (
                <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[16px] text-red-700 whitespace-pre-line">
                  {deleteProductError}
                </div>
              )}

              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => setConfirmDeleteProduct(null)}
                  className="flex-1 rounded-full border border-slate-300 bg-white px-4 py-2 text-[16px] font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteProduct}
                  disabled={deleteProductStatus === "loading"}
                  className="flex-1 rounded-full bg-[#FF3C3F] px-4 py-2 text-[16px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {deleteProductStatus === "loading" ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add product popup */}
      {addProductOpen && (
        <div className="fixed inset-0 z-50">
          <button
            type="button"
            className="absolute inset-0 bg-black/30"
            onClick={() => setAddProductOpen(false)}
            aria-label="Close"
          />
          <div className="relative mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-10">
            <div className="w-full max-w-lg rounded-2xl border border-[#E6EAF0] bg-white px-8 py-10 text-[16px] shadow-xl">
              <div className="mb-4 flex items-center justify-between">
                <p className="text-[16px] font-semibold text-slate-900">
                  Add to list
                </p>
                <button
                  type="button"
                  onClick={() => setAddProductOpen(false)}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#E6EAF0] bg-white p-0 text-slate-500 hover:bg-[#F5F7FB] hover:text-slate-800"
                >
                  ✕
                </button>
              </div>

              {!!addProductError && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[16px] text-red-700 whitespace-pre-line">
                  {addProductError}
                </div>
              )}
              {!!updateProductError && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[16px] text-red-700 whitespace-pre-line">
                  {updateProductError}
                </div>
              )}
              {!!deleteProductError && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[16px] text-red-700 whitespace-pre-line">
                  {deleteProductError}
                </div>
              )}

              <div className="space-y-5">
                <div className="space-y-1">
                  <input
                    value={productId}
                    onChange={(e) => setProductId(e.target.value)}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-femty-primary"
                    placeholder="Product ID (SKU)"
                  />
                  {fieldErrors.product_id && (
                    <p className="text-[16px] text-red-600">{fieldErrors.product_id}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <input
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-femty-primary"
                    placeholder="Product Name"
                  />
                </div>
                <div className="space-y-1">
                  <select
                    value={productPlatform}
                    onChange={(e) => setProductPlatform(e.target.value)}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-femty-primary"
                  >
                    <option value="tesco">Tesco</option>
                    <option value="morrison">Morrisons</option>
                    <option value="asda">Asda</option>
                    <option value="sainsbury">Sainsbury's</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <input
                    value={productImageUrl}
                    onChange={(e) => setProductImageUrl(e.target.value)}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-femty-primary"
                    placeholder="Image URL"
                  />
                </div>
                <div className="space-y-1">
                  <input
                    value={productCategory}
                    onChange={(e) => setProductCategory(e.target.value)}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-femty-primary"
                    placeholder="Category (optional)"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <input
                      value={productPrice}
                      onChange={(e) => setProductPrice(e.target.value)}
                      inputMode="decimal"
                      className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-femty-primary"
                      placeholder="Price"
                    />
                  </div>
                  <div className="space-y-1">
                    <input
                      value={productWeight}
                      onChange={(e) => setProductWeight(e.target.value)}
                      className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-femty-primary"
                      placeholder="Weight"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <input
                    value={String(productQty)}
                    onChange={(e) =>
                      setProductQty(Math.max(1, Number(e.target.value || 1)))
                    }
                    inputMode="numeric"
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-femty-primary"
                    placeholder="Quantity"
                  />
                </div>

                <div className="pt-2 flex items-center justify-center gap-3">
                  <button
                    type="button"
                    disabled={addProductStatus === "loading"}
                    onClick={async () => {
                      const errs = {};
                      if (!productId.trim()) errs.product_id = "Product ID is required.";
                      setFieldErrors(errs);
                      if (Object.keys(errs).length > 0) return;

                      const parsedPrice =
                        productPrice === "" ? undefined : Number(productPrice);

                      try {
                        await dispatch(
                          addGroceryProduct({
                            grocery_list_id: id,
                            product_id: productId.trim(),
                                product_name: productName.trim() || productId.trim(),
                                platform: productPlatform,
                            image_url: productImageUrl.trim() || undefined,
                            category: productCategory.trim() || undefined,
                            price: Number.isNaN(parsedPrice) ? undefined : parsedPrice,
                            weight: productWeight.trim() || undefined,
                            quantity: productQty || 1,
                          })
                        ).unwrap();
                        rememberGroceryProductView({
                          listId: id,
                          productId: productId.trim(),
                          view: productPlatform ? "single" : "compare",
                          platform: normalizeGroceryProductPlatform(productPlatform),
                        });
                        dispatch(fetchGroceryListDetail({ id, force: true }));
                        setAddProductOpen(false);
                      } catch (e) {
                        // shown via addProductError
                      }
                    }}
                    className="rounded-md bg-[#7352C7] px-8 py-2 text-[16px] font-semibold text-white hover:bg-[#7352C7]-hover disabled:opacity-60"
                  >
                    {addProductStatus === "loading" ? "Adding..." : "Add"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        )}
      </PanelShell>

      {compareConfirmOpen && (
        <div className="fixed inset-0 z-[90]">
          <button
            type="button"
            aria-label="Close compare confirmation"
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              if (!compareSubmitting) setCompareConfirmOpen(false);
            }}
          />
          <div className="relative mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-10">
            <div className="relative w-full max-w-md rounded-2xl border border-[#E6EAF0] bg-white p-6 shadow-xl">
              <h3 className="text-lg font-semibold text-slate-900">Compare basket?</h3>
              <p className="mt-2 text-[16px] text-slate-600">
                Are you sure you want to compare?
                {comparisonUsage?.unlimited ? null : (
                  <> This will use 1 of your monthly basket comparisons.</>
                )}
              </p>
              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  disabled={compareSubmitting}
                  onClick={() => setCompareConfirmOpen(false)}
                  className="flex-1 rounded-full border border-slate-300 bg-white px-4 py-2 text-[16px] font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  No
                </button>
                <button
                  type="button"
                  disabled={compareSubmitting}
                  onClick={handleConfirmCompare}
                  className="flex-1 rounded-full bg-[#7352C7] px-4 py-2 text-[16px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {compareSubmitting ? "Starting…" : "Yes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {limitModalOpen && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl border border-[#E6EAF0] bg-white p-6 text-center shadow-xl">
            <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full border-2 border-amber-400 text-amber-500">
              <span className="text-[20px] font-bold">!</span>
            </div>
            <h3 className="text-[20px] font-bold text-[#24303A] sm:text-[22px]">Comparison limit</h3>
            <p className="mt-2 text-[15px] leading-relaxed text-[#6E7B86] sm:text-[16px]">
              {limitModalMessage || comparisonLimitMessage}
            </p>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-center">
              <button
                type="button"
                onClick={() => setLimitModalOpen(false)}
                className="rounded-full border border-[#E6EAF0] bg-white px-6 py-2 text-[15px] font-semibold text-[#24303A] sm:text-[16px]"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  setLimitModalOpen(false);
                  navigate("/subscription");
                }}
                className="rounded-full bg-[#7352C7] px-6 py-2 text-[15px] font-semibold text-white shadow-sm sm:text-[16px]"
              >
                Upgrade plan
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Settings;

